import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Collapse,
  Group,
  Stack,
  TagsInput,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronDown, IconPlus, IconX } from "@tabler/icons-react";
import { fetchAliasSuggestions } from "./mentionsApi";
import { updateBrand } from "./brandsApi";

/**
 * Edits the spellings that count as a mention of this brand.
 *
 * Lives on the detection page on purpose. This is the screen where someone reads "mentioned in
 * 4 of 7 checks" and asks why it is not 6 — and a missing alias is the most common answer.
 *
 * Collapsible because it is settings, not a reading: on a page whose job is to report numbers, a
 * permanently open editor of a list that changes twice a year outranks the numbers. The header
 * still carries the two facts worth seeing without opening it — how many spellings count, and
 * whether there is a suggestion waiting.
 */
export default function BrandAliasesCard({ brand, onSaved, collapsible = false }) {
  const [open, setOpen] = useState(!collapsible);
  const [aliases, setAliases] = useState(brand?.aliases ?? []);
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setAliases(brand?.aliases ?? []);
    setDirty(false);
  }, [brand?.id, brand?.aliases]);

  const loadSuggestions = useCallback(async () => {
    if (!brand?.id) return;
    try {
      setSuggestions(await fetchAliasSuggestions(brand.id));
    } catch {
      // Suggestions are a convenience; the editor works without them.
      setSuggestions([]);
    }
  }, [brand?.id]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const change = (next) => {
    setAliases(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateBrand(brand.id, { ...brand, aliases });
      setDirty(false);
      await loadSuggestions();
      onSaved?.(aliases);
    } finally {
      setSaving(false);
    }
  };

  const unusedSuggestions = suggestions.filter(
    (s) => !aliases.some((a) => a.toLowerCase() === s.alias.toLowerCase()),
  );

  const header = (
    <Group gap="xs" wrap="nowrap">
      {collapsible && (
        <IconChevronDown
          size={16}
          style={{
            opacity: 0.6,
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 150ms ease",
          }}
        />
      )}
      <Text fw={600}>Brand spellings</Text>
      <Badge size="sm" variant="light" color="gray">
        {aliases.length}
      </Badge>
      {unusedSuggestions.length > 0 && (
        <Badge size="sm" variant="light" color="blue">
          {unusedSuggestions.length} suggested
        </Badge>
      )}
    </Group>
  );

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" align="center" wrap="nowrap">
        {collapsible ? (
          <UnstyledButton
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            style={{ flex: 1, minWidth: 0 }}
          >
            {header}
          </UnstyledButton>
        ) : (
          header
        )}
        {dirty && (
          <Button size="xs" onClick={save} loading={saving}>
            Save
          </Button>
        )}
      </Group>

      <Collapse in={open}>
      <Text fz="xs" c="dimmed" mt="sm" mb="sm">
        A mention only counts when the answer uses one of these. Casing, punctuation and German
        umlaut spellings are handled automatically — add anything genuinely different, such as a
        legal form or a misspelling models keep producing.
      </Text>

      <TagsInput
        value={aliases}
        onChange={change}
        placeholder="Add a spelling and press Enter"
        clearable
      />

      {unusedSuggestions.length > 0 && (
        <Stack gap={6} mt="md">
          <Text fz="xs" c="dimmed">
            Seen in past answers — add any that are really you:
          </Text>
          <Group gap={6}>
            {unusedSuggestions.map((s) => (
              <Tooltip
                key={s.alias}
                label={`Seen in ${s.occurrences} analysed answer(s)`}
                withArrow
              >
                <Badge
                  variant="light"
                  style={{ cursor: "pointer" }}
                  rightSection={
                    <ActionIcon
                      size="xs"
                      variant="transparent"
                      aria-label={`Add ${s.alias}`}
                      onClick={() => change([...aliases, s.alias])}
                    >
                      <IconPlus size={12} />
                    </ActionIcon>
                  }
                  onClick={() => change([...aliases, s.alias])}
                >
                  {s.alias}
                </Badge>
              </Tooltip>
            ))}
          </Group>
          <Text fz="xs" c="dimmed">
            Spellings closer to one of your competitors are never suggested — accepting one would
            credit their mentions to you from then on.
          </Text>
        </Stack>
      )}

      {dirty && (
        <Group gap={6} mt="sm">
          <ActionIcon
            size="sm"
            variant="subtle"
            color="gray"
            aria-label="Discard changes"
            onClick={() => {
              setAliases(brand?.aliases ?? []);
              setDirty(false);
            }}
          >
            <IconX size={14} />
          </ActionIcon>
          <Text fz="xs" c="dimmed">
            Changes apply to future checks, not to samples already taken.
          </Text>
        </Group>
      )}
      </Collapse>
    </Card>
  );
}
